import { type ReactNode } from 'react';
import { Header } from '../ui/header';
import { TabBar } from '../ui/tab-bar';
import { Sidebar } from './sidebar';
import type { UserRole } from '@serviceready/shared';

interface AppShellProps {
  role: UserRole;
  title: string;
  subtitle?: string;
  children: ReactNode;
  headerAction?: ReactNode;
  topBanner?: ReactNode;
  className?: string;
}

export function AppShell({
  role,
  title,
  subtitle,
  children,
  headerAction,
  topBanner,
  className = '',
}: AppShellProps) {
  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--color-bg)_0%,_var(--color-gray-100)_100%)]">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:bg-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:text-[var(--color-primary)]"
      >
        Skip to content
      </a>

      {/* Desktop sidebar */}
      <Sidebar role={role} />

      {/* Content area — offset by sidebar on desktop */}
      <div className="md:ml-64 min-h-screen flex flex-col">
        {/* Header */}
        <Header
          title={title}
          subtitle={subtitle}
          role={role}
          rightAction={headerAction}
        />
        {topBanner}

        {/* Main content area */}
        <main
          id="main-content"
          className={`flex-1 overflow-y-auto px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-20 md:pb-6 animate-entrance-delay-1 ${className}`}
        >
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>

        {/* Bottom tab bar — mobile only */}
        <TabBar role={role} />
      </div>
    </div>
  );
}
